import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { Notice } from '@/components/admin/Notice';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/admin/states';
import {
  fetchNotifications,
  markNotificationRead,
  type AppNotification,
} from '@/features/notifications/api';
import { formatDateTime } from '@/lib/datetime';

/** 通知一覧（全ロール共通・受講生の通知一覧は §10 の必須項目 / M6）. */
export function NotificationsPage(): ReactElement {
  const { user } = useAuth();
  const isOffice = user?.active_role === 'office';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const query = useQuery({
    queryKey: ['notifications', page, unreadOnly],
    queryFn: () => fetchNotifications(page, unreadOnly),
  });

  async function onOpen(notification: AppNotification): Promise<void> {
    if (notification.read_at === null) {
      await markNotificationRead(notification.id);
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
    }
    if (notification.link) navigate(notification.link);
  }

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    return <ErrorState message="通知の取得に失敗しました。" onRetry={() => void query.refetch()} />;
  }

  const { items, total, per_page } = query.data;
  const totalPages = Math.max(1, Math.ceil(total / per_page));

  return (
    <>
      {isOffice && (
        <Notice kind="info" dismissible={false}>
          これは<strong>自分に届いた</strong>通知です。受講生などへ
          <strong>これから送られる通知・送られた通知</strong>を確認するときは、左メニューの
          <Link to="/notifications/schedules" className="underline">
            システム通知（配信予定・履歴）
          </Link>
          を開いてください。
        </Notice>
      )}
      <PageHeader
        title="自分宛のシステム通知"
        description="システムが自動で送ったお知らせのうち、自分宛に届いたものです（イベントのご案内、課題の締切、採点の公開など）。返信はできません。人とのやり取りは「メッセージ」をご覧ください。メールにも要約とリンクが届きますが、正しい内容はこの画面のものです。"
      />

      <div className="mb-3 flex items-center gap-3">
        <label className="flex items-center gap-1 text-wptext-muted">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => {
              setUnreadOnly(e.target.checked);
              setPage(1);
            }}
          />
          未読のみ表示
        </label>
        <span className="text-wptext-muted">{total}件</span>
      </div>

      {items.length === 0 ? (
        <EmptyState title="通知はありません" />
      ) : (
        <ul className="flex flex-col gap-1">
          {items.map((notification) => (
            <li key={notification.id}>
              <button
                type="button"
                onClick={() => void onOpen(notification)}
                className={`w-full rounded-sm border border-wpborder px-3 py-2 text-left hover:bg-wpcanvas ${
                  notification.read_at === null ? 'bg-white font-semibold' : 'bg-wpcanvas/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-wptext-strong">
                    {notification.read_at === null && (
                      <span className="mr-1 inline-block h-2 w-2 rounded-full bg-wpaccent align-middle" />
                    )}
                    {notification.title}
                  </span>
                  <span className="shrink-0 text-wptext-muted">
                    {formatDateTime(notification.created_at)}
                  </span>
                </div>
                <p className="mt-1 font-normal text-wptext-muted">{notification.body}</p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="btn-secondary px-2 py-1"
          >
            前へ
          </button>
          <span className="text-wptext-muted">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="btn-secondary px-2 py-1"
          >
            次へ
          </button>
        </div>
      )}
    </>
  );
}
